import { NavLink, useLocation } from 'react-router-dom';
import { useApp } from '@/context/AppContext';

interface NavItem {
  path: string;
  label: string;
  icon: string;
}

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: 'ri-dashboard-3-line' },
  { path: '/transfers', label: 'Money Transfers', icon: 'ri-exchange-dollar-line' },
  { path: '/clients', label: 'Clients', icon: 'ri-group-line' },
  { path: '/inventory', label: 'Inventory', icon: 'ri-store-2-line' },
  { path: '/sales', label: 'Sales', icon: 'ri-shopping-cart-2-line' },
  { path: '/reports', label: 'Reports', icon: 'ri-bar-chart-2-line' },
  { path: '/settings', label: 'Settings', icon: 'ri-settings-3-line' },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { isDark } = useApp();
  const location = useLocation();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full z-40 flex flex-col
          w-64 transition-transform duration-300
          ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}
          border-r
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0 lg:static lg:z-auto
        `}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-5 py-5 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          <img
            src="https://public.readdy.ai/ai/img_res/c249894e-aae9-4fee-8538-f8d51d41dbc4.png"
            alt="BizFlow Logo"
            className="w-9 h-9 rounded-lg object-cover"
          />
          <div>
            <h1 className={`font-bold text-base leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
              BizFlow
            </h1>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Business Dashboard</p>
          </div>
          <button
            className={`ml-auto lg:hidden w-7 h-7 flex items-center justify-center rounded-md ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}
            onClick={onClose}
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          <p className={`text-xs font-semibold uppercase tracking-wider px-3 mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Main Menu
          </p>
          <ul className="space-y-1">
            {navItems.slice(0, 5).map(item => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/'}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer
                    ${isActive
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : isDark
                        ? 'text-gray-300 hover:bg-gray-800 hover:text-white'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <p className={`text-xs font-semibold uppercase tracking-wider px-3 mb-3 mt-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Analytics
          </p>
          <ul className="space-y-1">
            {navItems.slice(5).map(item => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer
                    ${isActive
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : isDark
                        ? 'text-gray-300 hover:bg-gray-800 hover:text-white'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-base`} />
                  </span>
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div className={`px-5 py-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            &copy; 2026 BizFlow
          </p>
        </div>
      </aside>
    </>
  );
}
